/**
 * Simulation → presentation event bus.
 *
 * The sim never touches the renderer. Instead it appends plain data events to
 * `world.events` each tick; the renderer and the audio layer drain that list.
 * Events are cleared at the start of every tick, so nothing here can feed back
 * into the simulation and nothing here can break determinism.
 */

export const EV = {
  Hit: 0,
  Death: 1,
  Shoot: 2,
  Impact: 3,
  StructureHit: 4,
  StructureDown: 5,
  Breach: 6,
  Power: 7,
  Log: 8,
  Build: 9,
  Spawn: 10,
  WaveStart: 11,
  WaveEnd: 12,
  Rout: 13,
  Rally: 14,
  Levelup: 15,
  Block: 16,
  Heal: 17,
  Telegraph: 18,
  Boss: 19,
  PhaseChange: 20,
} as const;
export type EventKind = (typeof EV)[keyof typeof EV];

export interface SimEvent {
  k: EventKind;
  x: number;
  y: number;
  /** Direction of the blow, used for knockback nudges and particle cones. */
  dx: number;
  dy: number;
  /** Magnitude: damage for hits, radius for impacts, intensity for the rest. */
  a: number;
  /** Secondary scalar: unit type, power id, resource kind… context dependent. */
  b: number;
  team: number;
  text: string;
}

export const LogTone = {
  Neutral: 0,
  Good: 1,
  Bad: 2,
  Dire: 3,
  Lore: 4,
} as const;

/** Hard cap so a pathological tick cannot balloon memory. */
export const MAX_EVENTS_PER_TICK = 768;

export function pushEvent(
  out: SimEvent[],
  k: EventKind,
  x: number,
  y: number,
  a = 0,
  b = 0,
  team = 0,
  dx = 0,
  dy = 0,
  text = '',
): void {
  if (out.length >= MAX_EVENTS_PER_TICK) return;
  out.push({ k, x, y, dx, dy, a, b, team, text });
}

export function pushLog(out: SimEvent[], text: string, tone: number = LogTone.Neutral): void {
  if (out.length >= MAX_EVENTS_PER_TICK) return;
  out.push({ k: EV.Log, x: 0, y: 0, dx: 0, dy: 0, a: tone, b: 0, team: 0, text });
}
